import React, { useState, useEffect } from 'react'
import { Form, InputNumber, Button, Table, Alert, Space, message, Checkbox, Card, Divider } from 'antd'
import { DollarOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import moment from 'moment'
import { getOutstandingInvoices, allocatePayment } from '../../backend-integration/api/paymentAllocation'

const AllocationModal = ({ payment, onComplete, onCancel }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [invoices, setInvoices] = useState([])
  const [selectedInvoices, setSelectedInvoices] = useState([])
  const [allocations, setAllocations] = useState({})
  const [autoAllocate, setAutoAllocate] = useState(false)

  useEffect(() => {
    if (payment) {
      fetchOutstandingInvoices()
    }
  }, [payment])

  const fetchOutstandingInvoices = async () => {
    setLoading(true)
    try {
      const data = await getOutstandingInvoices({
        patientSearch: payment.description // Try to match patient from description
      })
      setInvoices(data)

      // Auto-allocate if option is selected
      if (autoAllocate) {
        performAutoAllocation(data)
      }
    } catch (error) {
      console.error('Error fetching invoices:', error)
      message.error('Failed to fetch outstanding invoices')
    } finally {
      setLoading(false)
    }
  }

  const performAutoAllocation = (invoiceList) => {
    let remainingAmount = payment.amount
    const newAllocations = {}
    const newSelectedInvoices = []

    // Sort invoices by date (FIFO)
    const sortedInvoices = [...invoiceList].sort((a, b) => new Date(a.invoiceDate) - new Date(b.invoiceDate))

    for (const invoice of sortedInvoices) {
      if (remainingAmount <= 0) break

      const outstandingAmount = invoice.outstandingAmount || invoice.totalAmount
      const allocationAmount = Math.min(remainingAmount, outstandingAmount)

      if (allocationAmount > 0) {
        newAllocations[invoice._id] = allocationAmount
        newSelectedInvoices.push(invoice._id)
        remainingAmount -= allocationAmount
      }
    }

    setAllocations(newAllocations)
    setSelectedInvoices(newSelectedInvoices)
  }

  const handleAllocationChange = (invoiceId, value) => {
    setAllocations((prev) => ({
      ...prev,
      [invoiceId]: value || 0
    }))
  }

  const handleSelectInvoice = (invoiceId, checked) => {
    if (checked) {
      setSelectedInvoices((prev) => [...prev, invoiceId])
      // Set initial allocation to full outstanding amount
      const invoice = invoices.find((inv) => inv._id === invoiceId)
      const outstanding = invoice.outstandingAmount || invoice.totalAmount
      const remainingPayment = payment.amount - getTotalAllocated()
      handleAllocationChange(invoiceId, Math.min(outstanding, remainingPayment))
    } else {
      setSelectedInvoices((prev) => prev.filter((id) => id !== invoiceId))
      handleAllocationChange(invoiceId, 0)
    }
  }

  const getTotalAllocated = () => {
    return Object.values(allocations).reduce((sum, amount) => sum + (amount || 0), 0)
  }

  const getRemainingAmount = () => {
    return payment.amount - getTotalAllocated()
  }

  const handleSubmit = async () => {
    const totalAllocated = getTotalAllocated()

    if (totalAllocated === 0) {
      message.error('Please allocate at least some amount')
      return
    }

    if (totalAllocated > payment.amount) {
      message.error('Total allocation exceeds payment amount')
      return
    }

    setLoading(true)
    try {
      const allocationData = {
        paymentId: payment._id,
        allocations: selectedInvoices
          .map((invoiceId) => ({
            invoiceId,
            amount: allocations[invoiceId] || 0
          }))
          .filter((alloc) => alloc.amount > 0)
      }

      await allocatePayment(allocationData)
      message.success('Payment allocated successfully')
      onComplete()
    } catch (error) {
      console.error('Error allocating payment:', error)
      message.error('Failed to allocate payment')
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      title: '',
      key: 'select',
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={selectedInvoices.includes(record._id)}
          onChange={(e) => handleSelectInvoice(record._id, e.target.checked)}
        />
      )
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo'
    },
    {
      title: 'Date',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      render: (date) => moment(date).format('YYYY-MM-DD')
    },
    {
      title: 'Patient',
      dataIndex: 'patientName',
      key: 'patientName'
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => `R ${amount.toFixed(2)}`,
      align: 'right'
    },
    {
      title: 'Outstanding',
      dataIndex: 'outstandingAmount',
      key: 'outstandingAmount',
      render: (amount, record) => `R ${(amount || record.totalAmount).toFixed(2)}`,
      align: 'right'
    },
    {
      title: 'Allocation',
      key: 'allocation',
      width: 150,
      render: (_, record) => (
        <InputNumber
          value={allocations[record._id] || 0}
          onChange={(value) => handleAllocationChange(record._id, value)}
          formatter={(value) => `R ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
          parser={(value) => value.replace(/R\s?|(,*)/g, '')}
          precision={2}
          min={0}
          max={record.outstandingAmount || record.totalAmount}
          disabled={!selectedInvoices.includes(record._id)}
          style={{ width: '100%' }}
        />
      )
    }
  ]

  return (
    <div>
      <Card size='small' style={{ marginBottom: 16 }}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <div>
            <strong>Payment Details:</strong>
          </div>
          <div>Reference: {payment.reference}</div>
          <div>Amount: R{payment.amount.toFixed(2)}</div>
          <div>Date: {moment(payment.paymentDate).format('YYYY-MM-DD')}</div>
          {payment.description && <div>Description: {payment.description}</div>}
        </Space>
      </Card>

      <div style={{ marginBottom: 16 }}>
        <Checkbox
          checked={autoAllocate}
          onChange={(e) => {
            setAutoAllocate(e.target.checked)
            if (e.target.checked) {
              performAutoAllocation(invoices)
            }
          }}
        >
          Auto-allocate (FIFO)
        </Checkbox>
      </div>

      <Table
        columns={columns}
        dataSource={invoices}
        rowKey='_id'
        loading={loading}
        pagination={false}
        scroll={{ y: 300 }}
        size='small'
      />

      <Divider />

      <Alert
        message={
          <Space style={{ width: '100%', justifyContent: 'space-between' }}>
            <span>Total Allocated: R{getTotalAllocated().toFixed(2)}</span>
            <span>Remaining: R{getRemainingAmount().toFixed(2)}</span>
          </Space>
        }
        type={getRemainingAmount() === 0 ? 'success' : 'info'}
        icon={<DollarOutlined />}
      />

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Button onClick={onCancel} icon={<CloseOutlined />}>
            Cancel
          </Button>
          <Button
            type='primary'
            onClick={handleSubmit}
            loading={loading}
            disabled={getTotalAllocated() === 0}
            icon={<CheckOutlined />}
          >
            Allocate Payment
          </Button>
        </Space>
      </div>
    </div>
  )
}

export default AllocationModal
